import { useId } from "react";
import { Eye, FileCheck2, Info } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Shared, factual disclosures for the ISO 27001 document templates.
 *
 * - <TemplateDevelopmentNotice />: how the policy templates were developed
 *   (AI-assisted drafts, each policy reviewed and revised by an ISO 27001
 *   consultant). Never implies certification, accreditation or endorsement.
 * - <PreviewDisclosure />: what a free preview shows versus what a purchase
 *   delivers. Format-aware so PDF and Excel previews are described accurately.
 *
 * Keep the wording here as the single source so every page stays consistent.
 */

export const TEMPLATE_DEVELOPMENT_TEXT =
  "Our ISO 27001 policy templates began as AI-assisted drafts. An ISO 27001 consultant then reviewed and revised each policy. The documents are editable starting points and must be checked and adapted to your organization's risks, processes, and legal needs. Purchasing them does not guarantee ISO 27001 compliance or certification.";

export function TemplateDevelopmentNotice({
  className,
  headingLevel = "h2",
}: {
  className?: string;
  headingLevel?: "h2" | "h3";
}) {
  const Heading = headingLevel;
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn("rounded-lg border border-border bg-muted/30 p-4 sm:p-5", className)}
    >
      <div className="flex items-start gap-3">
        <FileCheck2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1.5">
          <Heading
            id={headingId}
            className="text-sm sm:text-base font-semibold text-foreground"
          >
            How the policy templates were developed
          </Heading>
          <p className="text-sm text-muted-foreground leading-relaxed">{TEMPLATE_DEVELOPMENT_TEXT}</p>
        </div>
      </div>
    </section>
  );
}

export type PreviewFormat = "pdf" | "excel" | "none" | "pack";

export function previewDisclosureText(format: PreviewFormat): { preview: string; purchase: string } {
  switch (format) {
    case "pdf":
      return {
        preview:
          "The free preview shows only the first three pages of this document, rendered as a watermarked PDF preview. It is not the complete document.",
        purchase:
          "Buying this template (where offered) or the Document Pack gives you the complete, editable Word (.docx) file without the watermark, downloaded from your account after signing in with your checkout email.",
      };
    case "excel":
      return {
        preview:
          "The free preview shows only an abbreviated selection of the workbook's sheets and sample rows. It is not the complete workbook.",
        purchase:
          "Buying this template (where offered) or the Document Pack gives you the complete, editable Excel (.xlsx) workbook, downloaded from your account after signing in with your checkout email.",
      };
    case "none":
      return {
        preview: "No free preview is available for this document.",
        purchase:
          "The complete, editable file is included in the Document Pack, downloaded from your account after signing in with your checkout email.",
      };
    case "pack":
    default:
      return {
        preview:
          "Free previews show only part of each document: typically the first three watermarked pages of a policy PDF preview, or an abbreviated selection of sheets for an Excel workbook. Some documents have no preview.",
        purchase:
          "The Document Pack gives you the complete, editable Word and Excel files listed on this page, without watermarks, downloaded from your account after signing in with your checkout email.",
      };
  }
}

export function PreviewDisclosure({
  format,
  className,
}: {
  format: PreviewFormat;
  className?: string;
}) {
  const { preview, purchase } = previewDisclosureText(format);
  return (
    <div
      role="note"
      aria-label="What the preview includes and what you get after purchase"
      className={cn(
        "rounded-lg border border-border bg-card p-4 text-left text-sm text-muted-foreground space-y-2",
        className
      )}
    >
      <p className="flex items-start gap-2">
        <Eye className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          <strong className="text-foreground">Preview: </strong>
          {preview}
        </span>
      </p>
      <p className="flex items-start gap-2">
        <Info className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          <strong className="text-foreground">After purchase: </strong>
          {purchase}
        </span>
      </p>
    </div>
  );
}
